// src/components/js/PubmedComponent.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../css/PubmedComponent.css';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
const PAGE_SIZE = 10;
const yearIntervals = [
  { label: '1970–1980', start: 1970, end: 1980 },
  { label: '1981–1990', start: 1981, end: 1990 },
  { label: '1991–2000', start: 1991, end: 2000 },
  { label: '2001–2010', start: 2001, end: 2010 },
  { label: '2011–2020', start: 2011, end: 2020 },
  { label: '2021–2025', start: 2021, end: 2025 },
];

const PubmedComponent = () => {
  const [articles, setArticles] = useState([]);
  const [expandedAbstracts, setExpandedAbstracts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInterval, setSelectedInterval] = useState(null);
  const [selectedJournal, setSelectedJournal] = useState('');
  const [journalFacets, setJournalFacets] = useState([]);
  const navigate = useNavigate();

  // Fetch journal facets once
  useEffect(() => {
    const fetchFacets = async () => {
      try {
        const res = await axios.get('http://197.156.76.122/api/pubmed/facets/');
        setJournalFacets(res.data.journal_facets || []);
      } catch (err) {
        console.error('Failed to fetch facets', err);
      }
    };
    fetchFacets();
  }, []);

  // Fetch articles
  useEffect(() => {
    const fetchPubMedData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          page,
          page_size: PAGE_SIZE,
          q: searchQuery,
        };
        if (selectedInterval) {
          params.year_start = selectedInterval.start;
          params.year_end = selectedInterval.end;
        }
        if (selectedJournal && selectedJournal !== 'All') {
          params.journal = selectedJournal;
        }

        const response = await axios.get('http://197.156.76.122/api/pubmed/', { params });
        setArticles(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / PAGE_SIZE));
      } catch (err) {
        console.error(err);
        setError('Failed to fetch PubMed data.');
      } finally {
        setLoading(false);
      }
    };

    fetchPubMedData();
  }, [page, searchQuery, selectedInterval, selectedJournal]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const toggleAbstract = (pmid) => {
    setExpandedAbstracts(prev => ({ ...prev, [pmid]: !prev[pmid] }));
  };

  // 🔹 Pagination Controls Component (used at top & bottom)
  const PaginationControls = () => (
    <div className="pagination">
        {/* 🔙 Back to Home (ADD THIS HERE) */}
	    <button 
	      className="back-button"
	      onClick={() => navigate('/')}
	    >
	      <FaArrowLeft /> Back to Home
	    </button>
      <button disabled={page === 1} onClick={() => handlePageChange(1)}>⏮ First</button>
      <button disabled={page === 1} onClick={() => handlePageChange(page - 1)}>⬅ Previous</button>
      <span>Page {page} of {totalPages}</span>
      <button disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>Next ➡</button>
      <button disabled={page === totalPages} onClick={() => handlePageChange(totalPages)}>Last ⏭</button>
    </div>
  );

  return (
    <div className="pubmed-container">
      {/* Sidebar Filters */}
      <aside className="pubmed-sidebar">
        <h3>Filter</h3>

        <h4>By Publication Year Interval</h4>
        <label>
          <input
            type="radio"
            checked={!selectedInterval}
            onChange={() => { setSelectedInterval(null); setPage(1); }}
          />
          All
        </label>
        {yearIntervals.map(interval => (
          <label key={interval.label}>
            <input
              type="radio"
              checked={selectedInterval?.label === interval.label}
              onChange={() => { setSelectedInterval(interval); setPage(1); }}
            />
            {interval.label}
          </label>
        ))}

        <h4>By Journal</h4>
        <label>
          <input
            type="radio"
            checked={selectedJournal === '' || selectedJournal === 'All'}
            onChange={() => { setSelectedJournal('All'); setPage(1); }}
          />
          All
        </label>
        {journalFacets.map(j => (
          <label key={j.journal_or_book}>
            <input
              type="radio"
              checked={selectedJournal === j.journal_or_book}
              onChange={() => { setSelectedJournal(j.journal_or_book); setPage(1); }}
            />
            {j.journal_or_book} ({j.count})
          </label>
        ))}
      </aside>

      {/* Main Content */}
      <div className="pubmed-main">
        <div className="pubmed-header">
          <h2>📚 PubMed References <span className="subtitle">(from NCBI)</span></h2>
          <div className="pubmed-toolbar">
            <input
              type="text"
              placeholder="Search by Title or PMID..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {loading ? (
          <div>Loading PubMed articles...</div>
        ) : error ? (
          <div>{error}</div>
        ) : (
          <div className="results-wrapper">
            {/* 🔹 Pagination at Top */}
            <PaginationControls />

            <div className="results-meta">
              Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong>–
              <strong>{Math.min(page * PAGE_SIZE, totalCount)}</strong> of{' '}
              <strong>{totalCount}</strong> results
            </div>

            {articles.map(item => {
              const isExpanded = expandedAbstracts[item.pmid];
              const abstractPreview =
                item.abstract && item.abstract.length > 200
                  ? item.abstract.slice(0, 200) + '...'
                  : item.abstract;

              return (
                <div className="result-item" key={item.pmid}>
                  <div className="title-checkbox-container">
                    <a
                      href={item.doi ? `https://doi.org/${item.doi}` : `https://pubmed.ncbi.nlm.nih.gov/${item.pmid}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="result-title"
                    >
                      <strong>{item.title}</strong>
                    </a>
                  </div>

                  <div className="result-content">
                    <div>
                      {item.authors}. <i>{item.journal_or_book}</i>. {item.publication_year}.
                      &nbsp;<strong>PMID:</strong> {item.pmid}
                      {/* 🔹 Conditionally show PMCID */}
                      {item.pmcid && (
                        <>
                          &nbsp; | <strong>PMCID:</strong>{' '}
                          <a
                            href={`https://www.ncbi.nlm.nih.gov/pmc/articles/${item.pmcid}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.pmcid}
                          </a>
                        </>
                      )}
                    </div>

                    {item.abstract && (
                      <div className="abstract-text">
                        <strong>Abstract:</strong>{' '}
                        {isExpanded ? item.abstract : abstractPreview}
                        {item.abstract.length > 200 && (
                          <button onClick={() => toggleAbstract(item.pmid)}>
                            {isExpanded ? 'See less' : 'See more'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* 🔹 Pagination at Bottom */}
            <PaginationControls />
          </div>
        )}
      </div>
    </div>
  );
};

export default PubmedComponent;

